import { useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import {
  ArrowUpRight,
  Bookmark,
  CalendarCheck,
  CircleAlert,
  Clock,
  Clock3,
  Copy,
  Eye,
  Hand,
  Heart,
  Link2,
  Loader,
  MessageCircle,
  PenLine,
  Radio,
  RotateCw,
  Share2,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import {
  channelName,
  clockLabel,
  compactNumber,
  durationLabel,
  mediaOverlay,
  metricChips,
  numbersLine,
  PLATFORM_NAME,
  postStatus,
  routeChip,
  toneClass,
  type MetricKind,
} from "./liveModel";
import type { LiveChannel, LiveFile, LivePost as LivePostModel, LiveRoute } from "./liveTypes";
import { LIVE_FILE_DRAG } from "./liveModel";
import { FilePicture, MediaMark, PlatformMark, postWidth } from "./LiveParts";

const METRIC_ICON: Record<MetricKind, typeof Eye> = { views: Eye, likes: Heart, saves: Bookmark, comments: MessageCircle, shares: Share2 };
const METRIC_WORD: Record<MetricKind, string> = { views: "views", likes: "likes", saves: "saves", comments: "comments", shares: "shares" };
const TONE_ICON = { live: Radio, changed: PenLine, waiting: Clock } as const;
const ROUTE_ICON: Record<LiveRoute["state"], typeof Eye | null> = {
  "not-scheduled": Clock3,
  scheduled: CalendarCheck,
  manual: Hand,
  posting: Loader,
  failed: CircleAlert,
  connect: null,
};

/** The hand mark for a channel somebody posts themselves. It stays on the picture. */
function HandMark() {
  return (
    <span className="co-media-chip" aria-label="Manual">
      <Hand className="co-live-manual size-[13px]" strokeWidth={2} aria-hidden="true" />
    </span>
  );
}

/** A Media chip / Label on the picture: an icon and a few words, media glass. */
function StateChip({ icon: Icon, label, live = false, scheduled = false, manual = false, spin = false }: { icon: typeof Eye; label: string; live?: boolean; scheduled?: boolean; manual?: boolean; spin?: boolean }) {
  return (
    <span className="co-media-chip co-media-chip--label font-medium">
      <Icon
        className={cn(
          "size-[13px] shrink-0",
          live && "text-co-live",
          scheduled && "co-live-scheduled",
          manual && "co-live-manual",
          spin && "motion-safe:animate-[spin_1.6s_linear_infinite]",
        )}
        strokeWidth={2}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/** A label-only Media chip (Figma 08, the numbers' tiles): "Live since 18:06" in green, or "Not connected". */
function WordChip({ label, live = false }: { label: string; live?: boolean }) {
  // Inline: the chip's own ink is unlayered CSS and would beat a utility class.
  return (
    <span className="co-media-chip co-media-chip--label font-medium" style={live ? { color: "var(--co-live)" } : undefined}>
      {label}
    </span>
  );
}

/** "12.4K views · 980 likes · 41 comments": the numbers a source gives, in this order. */
function numbersWords(post: LivePostModel, kinds: MetricKind[]): string {
  return kinds
    .flatMap((kind) => {
      const value = post.metrics?.[kind];
      return typeof value === "number" ? [`${compactNumber(value)} ${METRIC_WORD[kind]}`] : [];
    })
    .join(" · ");
}

/** "Live since 18:02", with the day once it is from an earlier day (or always, on the results). */
function liveSinceLabel(iso: string, timeZone: string, withDay: boolean): string {
  return `Live since ${clockLabel(iso, timeZone, withDay || Date.now() - new Date(iso).getTime() > 20 * 60 * 60 * 1000)}`;
}

/** "Scheduled · in 40 min" inside the last hour, else the route's day and time. */
function routeWords(route: LiveRoute, timeZone: string): string | null {
  if (route.state === "scheduled" && route.at) {
    const minutes = Math.round((new Date(route.at).getTime() - Date.now()) / 60000);
    if (minutes > 0 && minutes < 60) return `Scheduled · in ${minutes} min`;
  }
  return routeChip(route, timeZone);
}

export type PostSize = "desk" | "tablet" | "phone" | "results";

const aspect = (ratio: string): CSSProperties => {
  const [w, h] = ratio.split(":").map(Number);
  return { aspectRatio: w && h ? `${w} / ${h}` : "9 / 16" };
};

/**
 * One channel's post on the Live tab (DESIGN.md "Live"): the picture at the
 * brief's shape with the platform's logo on media glass, the handle and the
 * caption over a scrim, and, once the kit is out, one line under it. A slot
 * with no file yet shows the shape it waits for. No channel's name is written
 * on the picture, and no ⋯ menu: a click opens the one editor.
 */
export default function LivePost({
  channel,
  post,
  file,
  slideFile,
  slideIndex,
  slideCount,
  size,
  selected = false,
  dim = false,
  onOpen,
  kitSent = false,
  timeZone,
  pending,
  emptyHint,
  results = false,
  width,
  fluid = false,
  onConnect,
  onRetry,
  onOpenLive,
}: {
  channel: LiveChannel;
  post: LivePostModel;
  file: LiveFile | null;
  /** A carousel shows the slide picked in its strip. */
  slideFile?: LiveFile | null;
  slideIndex?: number;
  slideCount?: number;
  size: PostSize;
  selected?: boolean;
  dim?: boolean;
  onOpen?: () => void;
  kitSent?: boolean;
  timeZone: string;
  /** A file on its way into this slot. */
  pending?: string | null;
  emptyHint: string;
  results?: boolean;
  width?: number;
  /** The parent sets the width (a row that shrinks before it wraps). */
  fluid?: boolean;
  /** Connect the platform's account (a channel with no account). */
  onConnect?: () => void;
  /** Try a failed channel again. */
  onRetry?: () => void;
  /** Opens a live post's numbers (its "Live since" is the button). Team only. */
  onOpenLive?: () => void;
}) {
  const shown = channel.kind === "CAROUSEL" ? (slideFile ?? file) : file;
  const overlay = mediaOverlay(post, channel);
  const bars = !!shown?.ratio && shown.ratio !== channel.ratio;
  const status = postStatus(post, { kitSent: kitSent || results, timeZone, withDay: results });
  // A live post carries its numbers on the picture, as soon as there are any;
  // the numbers' tiles write them under it instead (Figma 08).
  const chips = !results && post.liveSince ? metricChips(post.metrics, channel) : [];
  // Manual keeps its route after the post is live, so the hand is not replaced by "Live since".
  const route = !post.liveSince || post.route?.state === "manual" ? (post.route ?? null) : null;
  const routeIcon = route ? ROUTE_ICON[route.state] : null;
  const routeLabel = route ? routeWords(route, timeZone) : null;
  const platformName = PLATFORM_NAME[channel.platform] ?? channel.platform;
  // The one action this post needs, on the picture (Media button / Connect).
  const action: { label: string; run: () => void } | null =
    results
      ? null
      : route?.state === "connect" && onConnect
      ? { label: `Connect ${platformName}`, run: onConnect }
      : route?.state === "failed" && onRetry
        ? { label: "Try again", run: onRetry }
        : null;
  // The kit's line under the post says where it is; otherwise the picture does.
  const liveChip = !!post.liveSince && (results || !kitSent);
  // Why the post's numbers can't be read (a wrong or private link): the fix is another link.
  const linkError = post.tracking && !post.tracking.reading ? post.tracking.error : null;
  const w = width ?? (channel.kind === "CAROUSEL" && size === "desk" ? 360 : postWidth(channel.ratio, size));
  const name = channelName(channel);

  // A live chip that opens the numbers sits over the picture, outside the post's own button.
  const liveButton = liveChip && onOpenLive;
  const manual = route?.state === "manual";
  let topLeft: ReactNode = null;
  if (results && liveChip && !liveButton) {
    topLeft = <WordChip live label={liveSinceLabel(post.liveSince!, timeZone, false)} />;
  } else if (results && !post.liveSince && route?.state === "connect") {
    topLeft = <WordChip label="Not connected" />;
  } else if (liveChip && !liveButton) {
    topLeft = (
      <>
        {manual ? <HandMark /> : null}
        <StateChip icon={Radio} live label={liveSinceLabel(post.liveSince!, timeZone, results)} />
      </>
    );
  } else if (!liveChip && routeIcon && routeLabel) {
    topLeft = (
      <StateChip
        icon={routeIcon}
        label={routeLabel}
        scheduled={route?.state === "scheduled"}
        manual={route?.state === "manual" || route?.state === "not-scheduled"}
        spin={route?.state === "posting"}
      />
    );
  } else if (channel.kind === "CAROUSEL" && slideCount) {
    topLeft = <StateChip icon={Copy} label={`${(slideIndex ?? 0) + 1}/${slideCount}`} />;
  }

  const picture = (
    <div
      className="co-live-media w-full"
      data-empty={!shown || undefined}
      style={aspect(channel.ratio)}
      data-bars={bars || undefined}
      data-dimmed={(shown && !!action) || undefined}
    >
      {shown ? (
        <FilePicture file={shown} alt="" />
      ) : (
        <div className="co-live-layer absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-6 text-center">
          {pending ? (
            <>
              <span className="co-live-slot-tag uppercase">Uploading</span>
              <p className="w-[180px] max-w-full text-[12px] leading-[1.55] text-co-faint">{pending} lands here when it is in.</p>
            </>
          ) : (
            <>
              <span className="co-live-slot-tag">
                {[channel.kind === "CAROUSEL" ? `${channel.slides?.length ?? 0} × ${channel.ratio}` : channel.ratio, channel.kind === "VIDEO" ? durationLabel(channel.lengthSeconds) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              <p className="w-[180px] max-w-full text-[12px] leading-[1.55] text-co-faint">{emptyHint}</p>
            </>
          )}
        </div>
      )}
      <div className="co-live-layer pointer-events-none absolute inset-x-2.5 top-2.5 flex items-start gap-1">
        {topLeft}
        <MediaMark platform={overlay.chip.platform} label={overlay.chip.srLabel} className="ml-auto" />
      </div>
      {shown && results && overlay.lines[0]?.startsWith("@") ? (
        // The numbers' tiles keep only the handle on the picture.
        <div className="co-live-scrim">
          <p className="text-[13px] font-semibold">{overlay.lines[0]}</p>
        </div>
      ) : null}
      {shown && !results && !chips.length && overlay.lines.length ? (
        <div className="co-live-scrim">
          {overlay.lines.map((line, index) =>
            index === 0 && line.startsWith("@") ? (
              <p key={line} className="text-[13px] font-semibold">{line}</p>
            ) : (
              <p key={index} dir="auto" className="line-clamp-3 text-[12px] leading-[1.35] text-white/90">{line}</p>
            ),
          )}
        </div>
      ) : null}
      {chips.length ? (
        <div className="co-live-layer absolute inset-x-2.5 bottom-2.5 flex flex-wrap gap-1.5">
          {chips.map((chip) => {
            const Icon = METRIC_ICON[chip.kind];
            return (
              <span key={chip.kind} className="co-media-chip co-media-chip--label font-medium tabular-nums" aria-label={`${compactNumber(chip.value)} ${METRIC_WORD[chip.kind]}`}>
                <Icon className="size-[13px]" strokeWidth={2} aria-hidden="true" />
                <span aria-hidden="true">{compactNumber(chip.value)}</span>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  // The action sits over the picture, outside the post's own button.
  const actionButton = action ? (
    <div className="pointer-events-none absolute inset-0 z-[4] grid place-items-center">
      <button
        type="button"
        onClick={action.run}
        className="co-live-media-button pointer-events-auto"
        aria-label={`${action.label}${route?.state === "failed" ? `, the ${name} post` : ""}`}
      >
        {route?.state === "failed" && channel.platform !== "TIKTOK" ? (
          <RotateCw className="size-[18px]" strokeWidth={2} aria-hidden="true" />
        ) : (
          <PlatformMark platform={channel.platform} className="size-[18px]" />
        )}
        {action.label}
      </button>
    </div>
  ) : null;

  return (
    <article
      className="co-live-post flex shrink-0 flex-col"
      style={{ width: fluid ? "100%" : w }}
      data-selected={selected || undefined}
      data-dim={dim || undefined}
      aria-label={name}
    >
      <div className="relative">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            aria-label={`Edit the ${name} post`}
            aria-pressed={selected}
            className="co-live-post-hit block w-full text-left outline-none"
          >
            {picture}
          </button>
        ) : (
          picture
        )}
        {actionButton}
        {liveButton ? (
          <div className="pointer-events-none absolute inset-x-2.5 top-2.5 z-[3] flex items-start gap-1">
            {manual ? <HandMark /> : null}
            <button
              type="button"
              onClick={onOpenLive}
              aria-label={`${liveSinceLabel(post.liveSince!, timeZone, results)}. See the ${name} numbers`}
              className="co-live-chip-hit pointer-events-auto rounded-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
            >
              {results ? (
                <WordChip live label={liveSinceLabel(post.liveSince!, timeZone, false)} />
              ) : (
                <StateChip icon={Radio} live label={liveSinceLabel(post.liveSince!, timeZone, results)} />
              )}
            </button>
          </div>
        ) : null}
      </div>
      {results ? (
        // Figma 08, the cards flip to their numbers: two lines under the picture.
        <div className="mt-2 flex flex-col gap-2">
          {post.liveSince ? (
            <>
              <p className="text-[12.5px] font-semibold leading-[17.3px] text-co-text2">
                {numbersWords(post, ["views", "likes", "comments"]) || numbersLine(post, { timeZone }) || "No numbers from this platform"}
              </p>
              <p className="text-[12px] leading-[16.6px] text-co-muted">
                {numbersWords(post, ["shares", "saves"])}
                {post.permalink ? (
                  <>
                    {numbersWords(post, ["shares", "saves"]) ? " · " : ""}
                    <a
                      href={post.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-co-muted outline-none hover:text-co-text hover:underline focus-visible:underline"
                    >
                      Open
                      <span className="sr-only"> the {name} post</span>
                    </a>
                  </>
                ) : null}
              </p>
            </>
          ) : route?.state === "connect" ? (
            <p className="text-[12px] leading-[16.6px] text-co-muted">Creo did not post here</p>
          ) : status ? (
            <p className={cn("text-[12px] leading-[16.6px]", toneClass(status.tone))}>{status.text}</p>
          ) : null}
        </div>
      ) : null}
      {/* The first 24 hours carry everything on the picture; the kit's line says where a post is. */}
      {!results && kitSent && status ? (
        // The numbers live on the Analytics tab; the kit says where the post is.
        <>
          <div className="mt-2.5 flex items-center justify-between gap-2 text-[13px]">
            {(() => {
              const Icon = TONE_ICON[status.tone];
              const inner = (
                <>
                  <Icon className={cn("size-3.5 shrink-0", status.tone === "live" && "text-co-live")} strokeWidth={1.8} aria-hidden="true" />
                  <span className="truncate">{status.text}</span>
                </>
              );
              const tone = status.tone === "live" ? "text-co-text2" : toneClass(status.tone);
              return post.liveSince && onOpenLive ? (
                <button
                  type="button"
                  onClick={onOpenLive}
                  className={cn(
                    "flex min-w-0 items-center gap-2 underline decoration-co-line2 underline-offset-4 outline-none hover:text-co-text hover:decoration-current focus-visible:outline-2 focus-visible:outline-co-accent",
                    tone,
                  )}
                >
                  {inner}
                  <span className="sr-only">. See the {name} numbers</span>
                </button>
              ) : (
                <p className={cn("flex min-w-0 items-center gap-2", tone)}>{inner}</p>
              );
            })()}
            {post.liveSince && post.permalink ? (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1 text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
              >
                <ArrowUpRight className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                Open
                <span className="sr-only"> the {name} post</span>
              </a>
            ) : null}
          </div>
          {post.liveSince && linkError ? (
            <div className="mt-2 flex flex-col gap-1.5">
              <p className="text-[12.5px] leading-snug text-co-alert-ink">{linkError}</p>
              {onOpen ? (
                <button
                  type="button"
                  onClick={onOpen}
                  className="inline-flex items-center gap-1 self-start text-[12.5px] text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
                >
                  <Link2 className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                  Change link
                  <span className="sr-only"> of the {name} post</span>
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

/**
 * A carousel's slides in order (Live / Slide thumb: 84 wide at 4:5, radius 12,
 * 8 apart, the caption 6 under at 11.5). One strip for the post: every
 * platform posts the same order. Drag a slide to another place (or Alt and the
 * arrow keys) and it moves with its picture; a click shows that slide on the
 * posts. A slide a drag is over takes the accent, as the drop target.
 *
 * Editing (the team): a menu in the slide's top slot, files dropped from the
 * desktop onto a slide, and a trailing tile (Add slide).
 */
export function SlideStrip({
  slides,
  files,
  current,
  onPick,
  onReorder,
  pickLabel,
  onDropFiles,
  onDropFileId,
  slideMenu,
  emptyLabel,
  trailing,
}: {
  slides: { index: number; label: string; fileId: string | null; key?: string }[];
  files: Map<string, LiveFile>;
  current: number;
  onPick: (position: number) => void;
  onReorder?: (from: number, to: number) => void;
  /** A slot waiting on an answer reads "Cover, your pick". */
  pickLabel?: { index: number; label: string } | null;
  /** Pictures dropped from the desktop onto a slide (its 0-based position). */
  onDropFiles?: (position: number, files: File[]) => void;
  /** A picture on the post dragged from Files onto a slide (its 0-based position). */
  onDropFileId?: (position: number, fileId: string) => void;
  /** The slide's own menu, in the picture's top slot. */
  slideMenu?: (position: number) => ReactNode;
  /** What an empty slide says, when it says anything. */
  emptyLabel?: string;
  /** After the last slide (Add slide). */
  trailing?: ReactNode;
}) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  // A touch screen has no drag: press and hold lifts a slide, move it, let go
  // (Figma 08 phone carousel order, 13 Oct). The hold is 350 ms; the slide
  // under the finger is read from the point, not from events the browser
  // never sends to a captured pointer.
  const [lifted, setLifted] = useState<number | null>(null);
  const hold = useRef<number | null>(null);
  const slideAt = (x: number, y: number): number | null => {
    const hit = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-slide-index]");
    const index = hit ? Number(hit.dataset.slideIndex) : NaN;
    return Number.isFinite(index) ? index : null;
  };
  const endHold = () => {
    if (hold.current !== null) window.clearTimeout(hold.current);
    hold.current = null;
  };
  const move = (from: number, to: number) => {
    if (!onReorder || to < 1 || to > slides.length || from === to) return;
    onReorder(from, to);
    onPick(to - 1);
  };
  const carriesFiles = (event: DragEvent) => !!onDropFiles && Array.from(event.dataTransfer.types).includes("Files");
  const carriesFile = (event: DragEvent) => !!onDropFileId && Array.from(event.dataTransfer.types).includes(LIVE_FILE_DRAG);
  const takes = (event: DragEvent) => dragging !== null || carriesFiles(event) || carriesFile(event);
  return (
    <ol className="!m-0 flex list-none flex-wrap gap-2 !p-0" aria-label="Slides, in order">
      {slides.map((slide, position) => {
        const file = slide.fileId ? files.get(slide.fileId) : null;
        const waiting = pickLabel?.index === slide.index;
        const target = over === slide.index;
        return (
          <li key={slide.key ?? slide.index} data-slide-index={slide.index} className="group relative w-[84px]">
            <button
              type="button"
              draggable={!!onReorder}
              onPointerDown={(event) => {
                if (!onReorder || event.pointerType === "mouse") return;
                endHold();
                const target = event.currentTarget;
                const pointerId = event.pointerId;
                hold.current = window.setTimeout(() => {
                  hold.current = null;
                  setLifted(slide.index);
                  setDragging(slide.index);
                  setOver(slide.index);
                  try {
                    target.setPointerCapture(pointerId);
                  } catch {
                    /* an old browser: the move still reads the point */
                  }
                }, 350);
              }}
              onPointerMove={(event) => {
                if (lifted !== slide.index) return;
                const at = slideAt(event.clientX, event.clientY);
                if (at !== null) setOver(at);
              }}
              onPointerUp={() => {
                endHold();
                if (lifted !== slide.index) return;
                if (over !== null) move(slide.index, over);
                setLifted(null);
                setDragging(null);
                setOver(null);
              }}
              onPointerCancel={() => {
                endHold();
                if (lifted !== slide.index) return;
                setLifted(null);
                setDragging(null);
                setOver(null);
              }}
              onDragStart={(event) => {
                setDragging(slide.index);
                event.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onDragEnter={(event) => {
                if (!takes(event)) return;
                // Taking the drag on enter makes this slide the drop target.
                event.preventDefault();
                event.stopPropagation();
                setOver(slide.index);
              }}
              onDragOver={(event) => {
                if (!takes(event)) return;
                event.preventDefault();
                // The page takes a drop anywhere; a slide keeps its own.
                event.stopPropagation();
                event.dataTransfer.dropEffect = carriesFiles(event) ? "copy" : "move";
                setOver(slide.index);
              }}
              onDragLeave={() => setOver((value) => (value === slide.index ? null : value))}
              onDrop={(event) => {
                if (!takes(event)) return;
                event.preventDefault();
                event.stopPropagation();
                if (carriesFile(event)) {
                  const fileId = event.dataTransfer.getData(LIVE_FILE_DRAG);
                  if (fileId) onDropFileId!(position, fileId);
                } else if (carriesFiles(event)) {
                  const dropped = Array.from(event.dataTransfer.files ?? []).filter((entry) => entry.type.startsWith("image/"));
                  if (dropped.length) onDropFiles!(position, dropped);
                } else if (dragging !== null) {
                  move(dragging, slide.index);
                }
                setDragging(null);
                setOver(null);
              }}
              onClick={() => onPick(position)}
              onKeyDown={(event: KeyboardEvent) => {
                if (!event.altKey) return;
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  move(slide.index, slide.index - 1);
                } else if (event.key === "ArrowRight") {
                  event.preventDefault();
                  move(slide.index, slide.index + 1);
                }
              }}
              aria-current={current === position || undefined}
              aria-label={`Slide ${slide.index}, ${slide.label}${file ? `: ${file.name}` : ", empty"}${onReorder ? ". Alt and the arrow keys move it" : ""}`}
              data-dragging={dragging === slide.index || undefined}
              data-over={target || undefined}
              data-lifted={lifted === slide.index || undefined}
              className={cn(
                "co-live-slide co-live-media block w-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
                current === position && file && !target ? "co-live-ringed" : "",
                lifted === slide.index && "z-[2] scale-[1.06] shadow-[0_12px_28px_rgba(0,0,0,0.4)]",
              )}
              data-empty={!file || undefined}
              // A vertical swipe still scrolls the page; the held slide takes the sideways move.
              style={{ aspectRatio: "4 / 5", touchAction: onReorder ? "pan-y" : undefined }}
            >
              {file ? <FilePicture file={file} /> : null}
              {!file && emptyLabel ? (
                <span className="co-live-layer absolute inset-0 grid place-items-center px-2 text-center text-[11.5px] leading-[1.3] text-co-muted">
                  {emptyLabel}
                </span>
              ) : null}
            </button>
            {/* The picture's Top slot, at the right: shown on hover or focus, and
                while its menu is open; always on a touch screen, which has no hover. */}
            {slideMenu ? (
              <span className="absolute right-1.5 top-1.5 z-[3] flex items-start gap-1 opacity-0 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 has-[[aria-expanded=true]]:opacity-100 [@media(hover:none)]:opacity-100">
                {slideMenu(position)}
              </span>
            ) : null}
            <p
              className={cn(
                "mt-1.5 text-[11.5px] leading-[1.3]",
                waiting || target ? "text-co-accent" : "text-co-muted",
              )}
            >
              {slide.index} · {waiting ? pickLabel!.label : slide.label}
            </p>
          </li>
        );
      })}
      {trailing ? <li className="w-[84px]">{trailing}</li> : null}
    </ol>
  );
}
